<template>
	<div
		:class="[
			$style['app-text-field-container'],
			{
				[$style['app-text-field-container--multiple']]: multiple
			}
		]"
	>
		<div
			ref="textField"
			v-click-outside="inputBlurHandler"
			v-testid="'address-entry-field'"
			:style="zIndex"
			class="input"
			:class="[
				$style['app-text-field'],
				$style['app-text-field--dense'],
				{
					[$style['app-text-field--invalid']]: invalid,
					[$style['app-text-field--disabled']]: isDisabled,
					[$style['app-text-field--with-dropdown']]: list && list.length,
					[$style['app-text-field--focused']]: isFocused,
					[$style['app-text-field--with-search']]: search || leadingIcon,
					[$style['app-text-field--with-selected-flag']]: currentValue.flag
				}
			]"
		>
			<input
				ref="input"
				v-bind="$attrs"
				:disabled="isDisabled"
				:name="_uid"
				class="input"
				:class="[$style['app-text-field__input']]"
				:value="privateValue.name || currentValue.name"
				autocomplete="off"
				@keydown="keyupHandler"
				@focus="inputFocusHandler"
				@input="inputInputHandler"
			/>
			<input
				v-if="currentValue.id == null"
				ref="htmlInput"
				:name="_uid"
				autocomplete="off"
				:class="[
					$style['app-text-field__input'],
					$style['app-text-field__input--suggestion']
				]"
				:value="suggestion.name"
				disabled
			/>
			<FloatingLabel
				v-if="labelText"
				:class="[
					$style['app-text-field__label'],
					{
						[$style['app-text-field__label--with-search']] : (search || leadingIcon) && !isFloated
					}
				]"
				:label-text="labelText"
				:required="isRequired"
				:is-focused="isFocused"
				:is-floated="isFloated"
			/>
			<label
				v-if="!!!currentValue.name && !!!suggestion.name && !!!privateValue.name"
				:class="[$style['app-text-field__placeholder'], {
					[$style['app-text-field__placeholder--indented']] : search || leadingIcon
				}]"
			>
				{{ placeholder }}
			</label>
			<NotchedOutline
				:class="$style['app-text-field__outline']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
			<NotchedOutlineIdle
				:class="$style['app-text-field__idle']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>

			<div
				v-if="!!currentValue.name && !isDisabled && isCancelable"
				:class="$style['app-text-field__clear']"
				@click="clearInput"
			/>

			<div
				v-if="search"
				:class="$style['app-text-field__search']"
			>
				<FontAwesomeIcon :icon="['far', 'search']" />
			</div>
			<div
				v-else-if="leadingIcon"
				:class="$style['app-text-field__leading-icon']"
			>
				<FontAwesomeIcon :icon="leadingIcon" />
			</div>
			<div
				v-if="trailingIcon && !currentValue.name"
				:class="$style['app-text-field__trailing-icon']"
				@click="$emit('trailing-icon:click', $event)"
			>
				<FontAwesomeIcon :icon="trailingIcon" />
			</div>

			<div
				v-if="currentValue.flag"
				:class="$style['app-text-field__selected-flag']"
			>
				<Flag :code="currentValue.flag" />
			</div>

			<FontAwesomeIcon
				v-if="!isDisabled && hasDropdownOptions && (!isCancelable || (showCarret && !currentValue.name))"
				:icon="['fas', 'caret-down']"
				:class="[
					$style['app-text-field__dropdown'],
					!expanded && $style['app-text-field__dropdown--collapsed']
				]"
				@click="toggleDropdown"
			/>

			<DropdownMenu
				ref="dropdown"
				:class="[$style['dropdown'], { [$style['dropdown--top']]: top }]"
				:auto-position="shouldUseFixed"
				:min-width="0"
				:position="top ? 'top' : undefined"
				:handle-offset-click="false"
				@close="handleDropdownClose"
				@offset:click="handleDropdownOffsetClick"
			>
				<small
					v-if="!loading && searchCaption"
					:class="$style['dropdown__caption']"
				>
					{{ searchCaption }}
				</small>
				<small
					v-if="loading"
					style="padding: 10px;"
					:class="$style['subtitle']"
				>
					{{ $tc('common.messages.loading', 2) }}
				</small>
				<ul
					v-else
					v-testid="'dropdown-input-list'"
					:class="$style['dropdown__list']"
				>
					<template v-if="hasGroups">
						<template v-for="(group, groupIndex) in groupedFilteredList">
							<li
								v-if="group.items && group.items.length"
								:key="`group-header-${groupIndex}`"
								:class="$style['dropdown__list__group-header']"
							>
								{{ group.label }}
							</li>
							<li
								v-for="item in group.items"
								:key="`${groupIndex}-${item.id || item.name}`"
								v-testid="'dropdown-input-option'"
								:class="[
									$style['dropdown__list__item'],
									{
										[$style['dropdown__list__item--active']]: activeItem === item._flatIndex,
										[$style['dropdown__list__item--licence-plate']]: item.style === 'licence-plate'
									}
								]"
								@mouseover="activeItem = item._flatIndex"
								@click="inputBlurHandler($event, item)"
							>
								<div
									v-if="showChildrenCount"
									:class="$style['dropdown__list__item__icon']"
								>
									<FontAwesomeIcon
										v-if="item.childCount > 0"
										:icon="['fal', 'folder-open']"
									/>
								</div>
								<div
									v-if="item.icon && item.style !== 'licence-plate'"
									:class="$style['dropdown__list__item__icon']"
								>
									<FontAwesomeIcon
										:icon="item.icon"
									/>
								</div>
								<BaseCheckbox
									v-if="showCheckboxes"
									style="margin-right: 10px;"
									:value="!!checked.find(box => box.value.id === item.id)"
									:checked="!!checked.find(box => box.value.id === item.id)"
									@change="handleCheckbox(item, $event)"
									@click.native.stop
								/>

								<!-- Regular item name -->
								<template v-if="item.style !== 'licence-plate'">
									<div
										:class="$style['dropdown__list__item-name']"
										:style="[showCheckboxes ? { paddingBottom: '2px' } : '']"
									>
										<span>
											{{ item.name }}
										</span>
									</div>
									<div
										v-if="item.flag"
										:class="$style['dropdown__list__item__flag']"
									>
										<Flag :code="item.flag" />
									</div>
								</template>

								<!-- Licence plate styled item -->
								<div
									v-else
									:class="$style['licence-plate']"
								>
									<div :class="$style['uk-badge']">
										<div :class="$style['uk-flag']">
											<img
												:src="require('@/assets/images/flags/en-GB.svg')"
												alt="UK"
											/>
										</div>
										<div :class="$style['uk-text']">
											UK
										</div>
									</div>
									<div :class="$style['plate-text']">
										{{ item.name }}
									</div>
								</div>

								<span
									v-if="item.subtitle"
									:class="$style['subtitle']"
								>
									{{ item.subtitle }}
								</span>
								<span
									v-if="showChildrenCount && item.childCount > 0"
									:class="$style['dropdown__list__item__childrens']"
								>
									{{ item.childCount }}
								</span>
							</li>
							<li
								v-if="group.items && group.items.length && groupIndex < groupedFilteredList.length - 1 && groupedFilteredList.slice(groupIndex + 1).some(g => g.items && g.items.length)"
								:key="`group-divider-${groupIndex}`"
								:class="$style['dropdown__list__divider']"
							/>
						</template>
					</template>
					<template v-else>
						<li
							v-for="(item, index) in renderedList"
							:key="index"
							v-testid="'dropdown-input-option'"
							:class="[
								$style['dropdown__list__item'],
								{
									[$style['dropdown__list__item--active']]: activeItem === index,
									[$style['dropdown__list__item--licence-plate']]: item.style === 'licence-plate'
								}
							]"
							@mouseover="activeItem = index"
							@click="inputBlurHandler($event, item)"
						>
							<div
								v-if="showChildrenCount"
								:class="$style['dropdown__list__item__icon']"
							>
								<FontAwesomeIcon
									v-if="item.childCount > 0"
									:icon="['fal', 'folder-open']"
								/>
							</div>
							<div
								v-if="item.icon && item.style !== 'licence-plate'"
								:class="$style['dropdown__list__item__icon']"
							>
								<FontAwesomeIcon
									:icon="item.icon"
								/>
							</div>
							<BaseCheckbox
								v-if="showCheckboxes"
								style="margin-right: 10px;"
								:value="!!checked.find(box => box.value.id === item.id)"
								:checked="!!checked.find(box => box.value.id === item.id)"
								@change="handleCheckbox(item, $event)"
								@click.native.stop
							/>

							<!-- Regular item name -->
							<template v-if="item.style !== 'licence-plate'">
								<div
									:class="$style['dropdown__list__item-name']"
									:style="[showCheckboxes ? { paddingBottom: '2px' } : '']"
								>
									<span>
										{{ item.name }}
									</span>
								</div>
								<div
									v-if="item.flag"
									:class="$style['dropdown__list__item__flag']"
								>
									<Flag :code="item.flag" />
								</div>
							</template>

							<!-- Licence plate styled item -->
							<div
								v-else
								:class="$style['licence-plate']"
							>
								<div :class="$style['uk-badge']">
									<div :class="$style['uk-flag']">
										<img
											:src="require('@/assets/images/flags/en-GB.svg')"
											alt="UK"
										/>
									</div>
									<div :class="$style['uk-text']">
										UK
									</div>
								</div>
								<div :class="$style['plate-text']">
									{{ item.name }}
								</div>
							</div>

							<span
								v-if="item.subtitle"
								:class="$style['subtitle']"
							>
								{{ item.subtitle }}
							</span>
							<span
								v-if="showChildrenCount && item.childCount > 0"
								:class="$style['dropdown__list__item__childrens']"
							>
								{{ item.childCount }}
							</span>
						</li>
						<li
							v-if="hasMoreItemsThanRendered"
							:class="[$style['dropdown__list__item'], $style['dropdown__list__item--hint']]"
						>
							{{ $t('common.messages.keep-typing-to-refine') }}
						</li>
					</template>
					<div
						v-if="additionalFields"
						:class="$style['additional-fields']"
					>
						<small
							v-if="additionalFieldsCaption"
							:class="$style['subtitle']"
						>
							{{ additionalFieldsCaption }}
						</small>
						<li
							v-for="(item, index) in additionalFields"
							:key="index+(hasGroups ? allFilteredItems.length : filteredList.length)"
							:class="[
								$style['dropdown__list__item'],
								$style['dropdown__list__item--additional']
							]"
							@mouseover="activeItem = index+(hasGroups ? allFilteredItems.length : filteredList.length)"
							@click="handleAdditionalLinkClick($event, item)"
						>
							<FontAwesomeIcon
								v-if="item.icon"
								:icon="item.icon"
								style="margin-right: 10px;"
							/>
							{{ item.title }}
						</li>
					</div>
				</ul>
				<small
					v-if="showNoResults && filterQuery && (hasGroups ? !allFilteredItems.length : !filteredList.length) && !loading"
					:class="$style['subtitle']"
				>
					{{ $t('common.messages.no-results') }}
				</small>
			</DropdownMenu>
		</div>
		<p
			v-if="helperText"
			:class="[
				$style['app-text-field-helper-text'],
				invalid && $style['app-text-field-helper-text--invalid']
			]"
		>
			{{ helperText }}
		</p>
	</div>
</template>

<script>
const EXCLUDED_KEY_CODES = new Set(['9', '20', '16', '17', '18', '144', '145', '255', '33', '34', '35', '36', '37', '38', '39', '40', '45', '94', '95', '112', '113', '114', '115', '116', '117', '118', '119', '120', '121', '122', '123'])
const FILTER_DEBOUNCE_MS = 150
const MAX_RENDERED_ITEMS = 100

function normalize (phrase) {
	if (!phrase) return
	return phrase.normalize('NFKD').replace(/[^\w\s!-}\/]/g, '')
}

import NotchedOutline from '@c:essentials/NotchedOutline'
import NotchedOutlineIdle from '@c:essentials/NotchedOutlineIdle'
import FloatingLabel from '@c:essentials/FloatingLabel'
import DropdownMenu from '@c:essentials/DropdownMenu'
import Flag from '@/components/Essentials/PhoneNumberField/Flag'
import requiredField from '@/mixins/requiredField'

export default {
	name: 'BaseDropdownInput',
	components: {
		NotchedOutline,
		NotchedOutlineIdle,
		FloatingLabel,
		DropdownMenu,
		Flag
	},
	mixins: [requiredField],
	inject: {
		baseTableStickyHeader: {
			default: false
		},
		setBaseTableRowStatusDropdownOpen: {
			default: null
		}
	},
	inheritAttrs: false,
	$_veeValidate: {
		value () {
			if (!this.value) {
				return null
			}

			if (typeof this.value !== 'object') {
				return this.value
			}

			return this.value.id || this.value.name || null
		}
	},
	props: {
		additionalFields: {
			type: Array,
			default: () => []
		},
		additionalFieldsCaption: {
			type: [String, Number],
			default: null
		},
		searchCaption: {
			type: [String, Number],
			default: null
		},
		avoidFiltering: {
			type: Boolean,
			default: false
		},
		showCheckboxes: {
			type: Boolean,
			default: false
		},
		placeholder: {
			type: [String, Number],
			default: null
		},
		trailingIcon: {
			type: [Array, Object],
			default: null
		},
		leadingIcon: {
			type: [Array, Object],
			default: null
		},
		search: {
			type: [Boolean, String],
			default: false
		},
		top: {
			type: [Boolean, String],
			default: false
		},
		fixed: {
			type: Boolean,
			default: undefined
		},
		showChildrenCount: {
			type: Boolean,
			default: false
		},
		isCancelable: {
			type: Boolean,
			default: true
		},
		clearOnBlur: {
			type: Boolean,
			default: false
		},
		loading: {
			type: Boolean,
			default: false
		},
		showCarret: {
			type: Boolean,
			default: true
		},
		showNoResults: {
			type: Boolean,
			default: false
		},
		groups: {
			type: Array,
			default: () => []
		},
		list: {
			type: Array,
			default: () => []
		},
		value: {
			type: Object,
			default: () => ({
				name: null,
				id: null,
				flag: null
			})
		},
		labelText: {
			type: String,
			default: null
		},
		helperText: {
			type: String,
			default: null
		},
		multiple: {
			type: [Boolean, String],
			default: false
		},
		invalid: {
			type: [Boolean, String],
			default: false
		},
		isStatusDropdown: {
			type: Boolean,
			default: false
		}
	},
	data: () => ({
		isFocused: false,
		isFloated: false,
		expanded: false,
		checked: [],
		privateValue: {
			name: null,
			id: null,
			flag: null
		},
		showFullList: false,
		activeItem: -1,
		filterQuery: null
	}),
	computed: {
		currentValue () {
			return this.value || { name: null, id: null, flag: null }
		},
		availableList () {
			return (this.list || []).filter(item => item && item.name && !item.disabled)
		},
		hasGroups () {
			return Array.isArray(this.groups) && this.groups.length > 0
		},
		groupedFilteredList () {
			if (!this.hasGroups) return []
			let flatIndex = 0
			const mapItems = items => {
				return (items || []).filter(item => item && item.name && !item.disabled).map(item => ({
					...item,
					_flatIndex: flatIndex++
				}))
			}
			if (this.showFullList || this.avoidFiltering || !this.filterQuery) {
				return this.groups.map(group => ({
					...group,
					items: mapItems(group.items)
				}))
			}
			const normalizedQuery = normalize(this.filterQuery)
			const loweredQuery = normalizedQuery.toLowerCase()
			const result = normalizedQuery.split(' ').reduce((res, _item) => {
				return `${res}(?=.*${_item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`
			}, '')
			const regex = new RegExp(result, 'i')
			return this.groups.map(group => {
				const items = []
				for (const item of group.items || []) {
					if (!item || !item.name || item.disabled) continue
					const normalizedName = normalize(item.name)
					if (!regex.test(normalizedName)) continue
					if (normalizedName.toLowerCase().startsWith(loweredQuery)) {
						items.push({ ...item, weight: 1 })
					} else {
						items.push(item)
					}
				}
				items.sort((a, b) => {
					const nameA = (a.name || '').toUpperCase()
					const nameB = (b.name || '').toUpperCase()
					if (a.weight || b.weight) {
						if ((a.weight || 0) > (b.weight || 0)) return -1
						return 1
					}
					if (nameA < nameB) return -1
					if (nameA > nameB) return 1
					return 0
				})
				return {
					...group,
					items: items.map(item => ({
						...item,
						_flatIndex: flatIndex++
					}))
				}
			})
		},
		allFilteredItems () {
			if (!this.hasGroups) return this.filteredList
			const items = []
			for (const group of this.groupedFilteredList) {
				for (const item of group.items || []) {
					items.push(item)
				}
			}
			return items
		},
		suggestion () {
			if (this.loading || (!this.isFocused && !this.expanded)) {
				return { name: null, id: null, flag: null }
			}
			const b = this.privateValue.name || this.currentValue.name
			const compareValue = normalize(b)
			if (!compareValue) {
				return { name: null, id: null, flag: null }
			}
			const lowered = compareValue.toLowerCase()
			const source = this.hasGroups ? this.allFilteredItems : this.filteredList
			let exactMatch = null
			let likelyMatch = null
			let normalizedPrefixMatch = null
			for (const item of source) {
				if (!item || !item.name) continue
				const normalizedLoweredName = normalize(item.name).toLowerCase()
				if (normalizedLoweredName === lowered) {
					exactMatch = item
					break
				}
				if (!likelyMatch && item.name.toLowerCase().startsWith(lowered)) {
					likelyMatch = item
				}
				if (!normalizedPrefixMatch && normalizedLoweredName.startsWith(lowered)) {
					normalizedPrefixMatch = item
				}
			}
			const match = exactMatch || likelyMatch || normalizedPrefixMatch
			return match ? { ...match } : { name: null, id: null, flag: null }
		},
		isDisabled () {
			const disabledByAttr = this.$attrs.disabled === '' || this.$attrs.disabled
			const hasAdditionalFields = Array.isArray(this.additionalFields) && this.additionalFields.length > 0
			if (this.hasGroups) {
				const hasItems = this.groups.some(group => (group.items || []).some(item => item && item.name && !item.disabled))
				const disabledByEmptyList = !this.search && !hasItems && !hasAdditionalFields
				return disabledByAttr || disabledByEmptyList
			}
			const disabledByEmptyList = !this.search && !this.availableList.length && !hasAdditionalFields
			return disabledByAttr || disabledByEmptyList
		},
		hasDropdownOptions () {
			const hasAdditionalFields = Array.isArray(this.additionalFields) && this.additionalFields.length > 0
			if (this.hasGroups) {
				const hasItems = this.groups.some(group => (group.items || []).some(item => item && item.name && !item.disabled))
				return hasItems || hasAdditionalFields
			}
			return this.availableList.length > 0 || hasAdditionalFields
		},
		zIndex () {
			return { 'z-index': this.isFocused ? '100' : '1' }
		},
		shouldUseFixed () {
			return this.fixed !== undefined ? this.fixed : !!this.baseTableStickyHeader
		},
		filteredList () {
			if (this.showFullList || this.avoidFiltering) {
				return this.availableList
			}
			if (!this.list) return []
			const normalizedQuery = this.filterQuery ? normalize(this.filterQuery) : ''
			const query = normalizedQuery ? normalizedQuery.split(' ') : []
			const result = query.reduce((res, _item) => {
				return `${res}(?=.*${_item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`
			}, '')
			const arrayOfQueries = new RegExp(result, 'i')
			const loweredQuery = normalizedQuery.toLowerCase()
			const filtered = []
			for (const item of this.availableList) {
				const normalizedName = normalize(item.name)
				if (!arrayOfQueries.test(normalizedName)) continue
				if (loweredQuery && normalizedName.toLowerCase().startsWith(loweredQuery)) {
					filtered.push({ ...item, weight: 1 })
				} else {
					filtered.push(item)
				}
			}
			return filtered.sort((a, b) => {
				const nameA = (a.name || '').toUpperCase()
				const nameB = (b.name || '').toUpperCase()

				if (a.weight || b.weight) {
					if ((a.weight || 0) > (b.weight || 0)) {
						return -1
					} else {
						return 1
					}
				}

				if (nameA < nameB) {
					return -1
				}
				if (nameA > nameB) {
					return 1
				}
				return 0
			})
		},
		renderedList () {
			if (this.filteredList.length > MAX_RENDERED_ITEMS) {
				return this.filteredList.slice(0, MAX_RENDERED_ITEMS)
			}
			return this.filteredList
		},
		hasMoreItemsThanRendered () {
			return this.filteredList.length > MAX_RENDERED_ITEMS
		}
	},
	watch: {
		value: {
			immediate: true,
			handler () {
				this.privateValue = this.value ? { ...this.value } : { name: null, id: null, flag: null }
				this.isFloated = !!(this.value && this.value.name) || !!this.privateValue.name || !!this.isFocused || !!this.placeholder
				if (!this.$_typing) {
					clearTimeout(this.$_filterDebounceTimer)
					this.filterQuery = this.value ? this.value.name : null
				}
			}
		},

		isFocused: {
			handler (newVal, _oldVal) {
				if (newVal) {
					this.showFullList = true
					this.open()
				}
			}
		},

		expanded (isOpen) {
			// Keep the parent BaseTableRow above the sticky header while this dropdown is open.
			if (this.setBaseTableRowStatusDropdownOpen && (this.isStatusDropdown || this.shouldUseFixed)) {
				this.setBaseTableRowStatusDropdownOpen(isOpen)
			}
		},
		isDisabled (newVal) {
			if (newVal) {
				this.isFocused = false
				this.close()
			}
		},

		filterQuery: {
			handler () {
				this.privateValue.name = this.matchTextWithSuggestion(this.privateValue.name)
			}
		},
		list: {
			handler () {
				this.privateValue.name = this.matchTextWithSuggestion(this.privateValue.name)
			}
		}
	},
	created () {
		this.$_filterDebounceTimer = null
		this.$_typing = false
	},
	beforeDestroy () {
		// Reset the parent BaseTableRow dropdown-open state if this dropdown was open on destroy.
		if (this.setBaseTableRowStatusDropdownOpen && this.expanded && (this.isStatusDropdown || this.shouldUseFixed)) {
			this.setBaseTableRowStatusDropdownOpen(false)
		}
		clearTimeout(this.$_filterDebounceTimer)
	},
	methods: {
		open () {
			if (this.$refs['dropdown']) {
				this.$refs['dropdown'].open()
				this.expanded = true
			}
		},

		close () {
			if (this.$refs['dropdown']) {
				this.$refs['dropdown'].close()
			}
			this.expanded = false
		},

		handleDropdownClose () {
			this.expanded = false
		},

		handleDropdownOffsetClick (e) {
			if (this.$refs.textField && this.$refs.textField.contains(e.target)) return
			this.close()
		},

		handleCheckbox (option, checked) {
			const value = { id: option.id, value: option }
			if (checked) {
				this.checked.push(value)
				this.$emit('check', value)
			} else {
				this.checked = this.checked.filter(box => box.value.id !== option.id)
				this.$emit('clear', value)
			}
		},


		handleAdditionalLinkClick (e, link) {
			this.close()
			link.click(e)
		},

		_scrollActiveItem (offset) {
			const el = this.$el.querySelector(`.${this.$style['dropdown__list__item--active']}`)
			if (el) {
				el.parentNode.scrollTop = el.offsetTop + offset
			}
		},

		keyupHandler (event) {
			if (!EXCLUDED_KEY_CODES.has(String(event.keyCode))) {
				this.$emit('keydown', event)
			}

			const list = this.hasGroups ? this.allFilteredItems : this.renderedList
			if (this.activeItem >= list.length) {
				this.activeItem = -1
			}

			switch (event.key) {
				case 'Enter': {
					const result = this.suggestion.name ? this.suggestion : list[0]
					this.inputBlurHandler(event, result || null)
					this.$refs.input.blur()
					break
				}
				case 'Tab':
					this.inputBlurHandler(event)
					break
				case 'Escape':
					this.close()
					break
				case 'ArrowDown':
					this.inputFocusHandler(event)
					this.open()
					if (this.activeItem < list.length - 1) {
						this.activeItem++
						this.privateValue = { ...list[this.activeItem] }
						this._scrollActiveItem(40)
					}
					break
				case 'ArrowUp':
					this.inputFocusHandler(event)
					if (this.activeItem > 0) {
						this.activeItem--
						this.privateValue = { ...list[this.activeItem] }
						this._scrollActiveItem(-40)
					} else {
						this.close()
						this.privateValue = { name: null, id: null, flag: null }
						this.$emit('input', this.privateValue)
						this.activeItem = -1
					}
					break
			}
		},

		matchTextWithSuggestion (string) {
			const length = string ? string.length : 0
			if (this.suggestion?.name) {
				return this.suggestion.name.slice(0, length)
			}
			return string
		},

		inputFocusHandler (event) {
			this.isFocused = true
			this.isFloated = true
			if (this.search) {
				this.open()
			}
			this.$emit('focus', event)
			this.$nextTick(() => {
				this.$refs.input.focus()
			})
		},

		handleSubmit (val = null, avoidIfChecked) {
			if (avoidIfChecked && this.showCheckboxes && this.checked.length) {
				return
			}
			const value = val || this.suggestion
			this.$emit('submit', { ...value })
			if (this.clearOnBlur) {
				this.privateValue = { name: null, id: null, flag: null }
			}
			this.$emit('input', {
				...this.privateValue,
				name: this.privateValue.name ? this.privateValue.name.trim() : null
			})
		},

		findMatchingItem (name) {
			if (!name) return null
			const compareValue = normalize(name)
			if (!compareValue) return null
			const lowered = compareValue.toLowerCase()
			const source = this.hasGroups ? this.allFilteredItems : this.filteredList
			for (const item of source) {
				if (!item || !item.name) continue
				if (normalize(item.name).toLowerCase() === lowered) {
					return item
				}
			}
			return null
		},

		inputBlurHandler (event, val = null) {
			if (!this.isFocused) {
				this.isFloated = !!this.currentValue.name || !!this.privateValue.name || !!this.placeholder
				return
			}

			clearTimeout(this.$_filterDebounceTimer)
			this.$_typing = false

			if (val) {
				this.privateValue = { ...val }
			} else {
				const typed = this.privateValue.name || this.currentValue.name
				const match = this.findMatchingItem(typed)
				this.privateValue = match ? { ...match } : { name: null, id: null, flag: null }
			}

			this.privateValue.name = this.matchTextWithSuggestion(this.privateValue.name)
			this.filterQuery = this.privateValue.name
			this.isFocused = false
			this.isFloated = !!this.currentValue.name || !!this.placeholder

			this.close()

			if (val) {
				this.handleSubmit(val)
			}

			if (
				(this.currentValue.id !== this.privateValue.id ||
					this.currentValue.name !== this.privateValue.name ||
					this.currentValue.flag !== this.privateValue.flag) &&
				!this.isDisabled
			) {
				this.$emit('input', this.privateValue)
			}
			this.$emit('blur', event)
			if (this.showCheckboxes && this.checked.length) {
				this.submitCheckboxes()
			}
		},

		submitCheckboxes () {
			if (this.showCheckboxes && this.checked.length) {
				this.clearInput()
				this.$emit('check-submit', this.checked)
				this.checked = []
			} else {
				this.$emit('submit', this.privateValue)
			}
		},

		clearInput () {
			clearTimeout(this.$_filterDebounceTimer)
			this.$_typing = false
			this.filterQuery = null
			this.privateValue = {
				name: null,
				id: null,
				flag: null
			}
			this.$emit('input', this.privateValue)
			this.$emit('clear')
		},

		inputInputHandler (e) {
			this.inputFocusHandler(e)
			this.showFullList = false
			this.activeItem = -1
			this.privateValue = {
				...this.privateValue,
				name: e.target.value,
				id: null,
				flag: null
			}
			this.$_typing = true
			this.filterQuery = this.privateValue.name
			clearTimeout(this.$_filterDebounceTimer)
			this.$_filterDebounceTimer = setTimeout(() => {
				this.$_typing = false
			}, FILTER_DEBOUNCE_MS)
			if (this.isFocused) {
				this.open()
			}
			this.$emit('input', this.privateValue)
		},

		toggleDropdown (e) {
			if (this.isDisabled) return
			this.showFullList = true
			if (this.expanded) {
				this.close()
				return
			}
			this.inputFocusHandler(e)
			this.open()
		}
	}
}
</script>

<style lang="scss" module>
/* stylelint-disable selector-class-pattern, no-descending-specificity */
.subtitle {
    color: getColor(caption);
    font-size: 12px;
    display: block;
    line-height: 1.3;
    margin-top: 2px;
    margin-left: auto;
}

.additional-fields {
    box-shadow: 0 -1px 2px 0 getColor(hover-overlay);

    & > .subtitle {
        padding: $gutter $gutter * 2;
        margin-top: 0;
    }
}

.dropdown {
    position: absolute;
    background-color: getColor(surface-elevated);
    transform-origin: top;
    left: 0;
    right: 0;
    top: 48px;
    z-index: 999;
    border-radius: 8px;

    @include dark-mode {
        border: 1px solid transparent;
        box-shadow: 0 0 0 1px getColor(border-darker), 0 8px 24px rgb(0, 0, 0, 50%), 0 2px 8px rgb(0, 0, 0, 30%);
    }

    &--top {
        bottom: 47px;
        top: auto;
        z-index: 0;
    }

    & > .subtitle {
        padding: $gutter $gutter * 2;
        margin-top: 0;
    }

    &__caption {
        font-size: 12px;
        display: block;
        color: getColor(caption);
        padding: $gutter $gutter * 2;
        padding-top: $gutter;
        overflow-wrap: break-word;
        min-width: 0;
    }

    &__list {
        list-style: none;
        padding: 0;
        margin: 0;
        overflow: auto;
        max-height: 200px;
        display: grid;
        grid-template-columns: minmax(0, 1fr);

        &__group-header {
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            color: getColor(caption);
            padding: $gutter $gutter * 2;
            padding-bottom: 0;
            letter-spacing: 0.05em;
        }

        &__divider {
            border-bottom: 1px solid getColor(border);
            margin: 4px $gutter * 2;
            list-style: none;
        }

        &__item {
            font-size: 14px;
            padding: $gutter $gutter * 2;
            display: flex;
            justify-content: flex-start;
            align-items: flex-start;
            flex-wrap: wrap;
            box-sizing: border-box;
            transition: background-color 0.12s ease;

            &__icon {
                height: 20px;
                width: 20px;
                margin-right: 5px;
                display: flex;
                align-items: center;
                flex-shrink: 0;
            }

            &__childrens {
                margin-left: auto;
                color: getColor(caption);
                flex-shrink: 0;
            }

            &--hint {
                color: getColor(caption);
                font-size: 12px;
                pointer-events: none;
            }

            &--active {
                cursor: pointer;
                background-color: getColor(hover-overlay);

                @include dark-mode {
                    background-color: getColor(hover-overlay-strong);
                }
            }

            &--licence-plate {
                padding: calc($gutter / 2) $gutter * 2;

                &.dropdown__list__item--active {
                    background-color: getColor(hover-overlay);

                    @include dark-mode {
                        background-color: getColor(hover-overlay-strong);
                    }
                }
            }

            &--additional {
                color: getColor(tertiary);

                &:hover {
                    cursor: pointer;
                    color: getColor(primary-hover);
                }
            }
        }

        &__item-name {
            min-width: 0;
            overflow-wrap: break-word;
        }

        &__item__flag {
            margin-left: auto;
            align-self: center;
            flex-shrink: 0;
            width: 20px;
            height: 14px;
            border-radius: 2px;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }
}

// UK Licence Plate Styles
.licence-plate {
    display: flex;
    background: #F5DD61;
    border-radius: 4px;
    overflow: hidden;
    height: 30px;
    width: 165px;
    min-width: 165px;
}

.uk-badge {
    background: #06C;
    width: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    flex-shrink: 0;
    padding: 6px 4px;
}

.uk-flag {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: #fff;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.uk-text {
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.5px;
    line-height: 1;
    font-family: Arial, Helvetica, sans-serif;
}

.plate-text {
    flex: 1;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    font-size: 16px;
    font-weight: 700;
    text-align: center;
    color: #5a5a5a;
    letter-spacing: 4px;
    font-family: 'Charles Wright', Arial, sans-serif;
    text-transform: uppercase;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.app-text-field-container {
    width: 100%;
    position: relative;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--multiple {
        padding: 0 $gutter * 1.25;
    }
}

.app-text-field-helper-text {
    position: relative;
    bottom: 10px;
    margin: 0 $gutter;
    color: getColor(helper-text);
    font-size: 12px;
    line-height: 1.25rem;
}

.app-text-field {
    position: relative;
    display: inline-flex;
    width: 100%;
    align-items: flex-end;
    box-sizing: border-box;
    margin: $gutter * 1.25 0;
    height: 56px;
    border: none;

    &:not(.app-text-field--disabled, .app-text-field--focused):hover {
        .app-text-field__outline {
            border-color: getColor(text);
        }

        .app-text-field__idle {
            border-color: getColor(text);
        }
    }

    &__label {
        z-index: 1;
        font-size: 14px;
    }

    &__dropdown {
        z-index: 2;
        width: 30px;
        position: absolute;
        color: getColor(helper-text);
        cursor: pointer;
        right: 10px;
        top: 16px;
        transform: rotate(-180deg);
        transition: transform 0.2s ease-in-out;

        &--collapsed {
            transform: rotate(0);
        }

        &:hover {
            cursor: pointer;
        }
    }

   &__clear {
        z-index: 2;
        height: 100%;
        width: 35px;
        position: absolute;
        right: 0;
        cursor: pointer;
        color: getColor(grey-hover);

        // Use CSS mask for theme-adaptive icon
        &::before {
            content: "";
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            width: 20px;
            height: 20px;
            background-color: currentcolor;
            mask-image: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 2 24 24"><path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/></svg>');
            mask-size: contain;
            mask-repeat: no-repeat;
            mask-position: center;
        }

        &:hover {
            cursor: pointer;
            opacity: 0.7;
        }
    }

    &__search {
        z-index: 2;
        opacity: 0.6;
        height: 100%;
        width: 35px;
        position: absolute;
        left: 15px;
        top: 15px;
    }

    &__leading-icon {
        z-index: 2;
        opacity: 0.6;
        height: 100%;
        width: 35px;
        position: absolute;
        left: 15px;
        top: 13px;
    }

    &__trailing-icon {
        z-index: 2;
        position: absolute;
        top: 15px;
        right: 15px;
        opacity: 0.6;
        cursor: pointer;
    }

    &__placeholder {
        z-index: 1;
        font-size: 14px;
        line-height: 1rem;
        font-weight: 500;
        position: absolute;
        bottom: 16px;
        left: 20px;
        right: auto;
        width: calc(100% - 50px);
        color: getColor(helper-text);
        text-overflow: ellipsis;
        white-space: nowrap;
        cursor: text;
        overflow: hidden;

        &--indented {
            left: 40px;
            right: 0;
        }
    }

    &__input {
        position: relative;
        z-index: 2;
        display: flex;
        box-sizing: content-box;
        border: none;
        border-radius: 0;
        background-color: transparent;
        font-size: 16px;
        line-height: 1.75rem;
        font-weight: 400;
        letter-spacing: 0.0094em;
        width: 100%;
        height: 30px;
        transition: opacity 0.18s cubic-bezier(0.4, 0, 0.2, 1);
        caret-color: getColor(tertiary);

        &--suggestion {
            position: absolute;
            z-index: 1;
            color: getColor(helper-text) !important;
            inset: -2px 0 0;
            padding: 0 20px;
            width: calc(100% - 45px);

            &::after {
                content: "";
                height: 100%;
                width: 100%;
                background-color: getColor(danger);
            }
        }

        &:focus {
            outline: none;
        }

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus {
            animation-name: autofill;
            animation-fill-mode: both;
        }
    }

    &:not(.app-text-field--disabled) {
        .app-text-field__idle {
            background-color: getColor(input-background);
        }

        .app-text-field__outline {
            background-color: getColor(input-background);
        }

        .app-text-field__input {
            color: getColor(text);
        }
    }

    &--with-dropdown {
        .app-text-field__input {
            padding-right: 25px !important;
        }
    }

    &--with-selected-flag {
        .app-text-field__input {
            padding-left: 40px !important;
        }

        &.app-text-field--with-search {
            .app-text-field__input {
                padding-left: 70px !important;
            }

            .app-text-field__search,
            .app-text-field__leading-icon {
                left: 42px;
                width: 20px;
            }

            .app-text-field__placeholder,
            .app-text-field__placeholder--indented {
                left: 70px;
                width: calc(100% - 95px);
            }

            .app-text-field__label--with-search {
                left: 70px;
                max-width: calc(100% - 95px);
            }
        }

        .app-text-field__selected-flag {
            position: absolute;
            left: 15px;
            right: auto;
            top: 50%;
            transform: translateY(-50%);
            z-index: 2;
            width: 20px;
            height: 14px;
            border-radius: 2px;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
        }
    }

    &--dense {
        height: 48px;

        .app-text-field__input {
            padding: $gutter $gutter * 2;
        }
    }

    &--no-helper {
        margin-bottom: $gutter * 1.25;
    }

    &--disabled {
        .app-text-field__idle {
            background-color: getColor(input-background-disabled);
        }

        .app-text-field__outline {
            background-color: getColor(input-background-disabled);
        }

        .app-text-field__input {
        	color: getColor(helper-text) !important;

            &--suggestion {
                color: getColor(helper-text) !important;
            }
        }

        .app-text-field__label {
            color: getColor(input-label-disabled);
        }
    }

    &--invalid {
        .app-text-field__input {
            caret-color: getColor(danger);
        }

        .app-text-field__outline {
            border-color: getColor(danger) !important;
        }

        .app-text-field__idle {
            border-color: getColor(danger) !important;
        }

        .app-text-field__label {
            color: getColor(danger) !important;
        }

        & ~ .app-text-field-helper-text {
            color: getColor(danger) !important;
        }
    }

    &--with-search {
        .app-text-field__input {
            padding-left: 40px;
        }

        .app-text-field__label--with-search {
            left: 40px;
            max-width: calc(100% - 45px);
        }
    }
}

@keyframes autofill {
    to {
        color: inherit;
        background: transparent;
    }
}
/* stylelint-enable selector-class-pattern, no-descending-specificity */
</style>
